import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import {
  Send, Paperclip, Loader2, Image as ImageIcon, Video,
  Music, FileText, X, CornerDownRight, Smile, Mic, AlertCircle,   LayoutTemplate,
  ShoppingBag,
} from "lucide-react";
import { MEDIA_KIND, MEDIA_LIMITS } from "../../utils/whatsappMedia";

// Lazy-load the emoji picker — it ships its own asset bundle and we don't
// want to inflate the chat page's initial JS for users who never open it.
const EmojiPicker = lazy(() => import("emoji-picker-react"));

/* ─── Reply preview helpers ─── */
const getReplyPreviewText = (msg) => {
  if (!msg) return "";
  if (msg.message_type === "button" || msg.message_type === "interactive") {
    return msg.button?.text || msg.button?.payload || msg.message_text || "Button reply";
  }
  if (msg.message_type === "template") {
    if (msg.message_text && msg.message_text !== `Template: ${msg.template_name || msg.template_data?.template_name}`) {
      return msg.message_text;
    }
    return msg.template_name || msg.template_data?.template_name || "Template message";
  }
  if (["image", "video", "document", "audio", "sticker"].includes(msg.message_type)) {
    const labels = { image: "📷 Photo", video: "🎥 Video", audio: "🎵 Audio", document: "📄 Document", sticker: "🎨 Sticker" };
    const base = labels[msg.message_type] || "📎 Media";
    const caption = msg.caption || msg.message_text;
    return caption ? `${base} · ${caption}` : base;
  }
  return msg.message_text || "Message";
};

const MAX_PREVIEW = 120;
const clamp = (v) => (!v ? "" : v.length > MAX_PREVIEW ? `${v.slice(0, MAX_PREVIEW)}…` : v);
const getSenderLabel = (msg) =>
  !msg ? "" : msg.direction === "inbound" ? (msg.customer_name || "Customer") : "You";

const PREVIEW_ICONS = {
  image: <ImageIcon size={18} />,
  video: <Video size={18} />,
  audio: <Music size={18} />,
  document: <FileText size={18} />,
};

/* Attach-menu config — order drives the menu render order. */
const ATTACH_MENU = [
  {
    kind: MEDIA_KIND.IMAGE,
    label: "Photos",
    sub: `JPG, PNG · up to ${MEDIA_LIMITS[MEDIA_KIND.IMAGE].maxLabel}`,
    icon: ImageIcon,
    color: "#0EA5E9",
    bg: "rgba(14,165,233,0.12)",
  },
  {
    kind: MEDIA_KIND.VIDEO,
    label: "Videos",
    sub: `MP4, 3GP · up to ${MEDIA_LIMITS[MEDIA_KIND.VIDEO].maxLabel}`,
    icon: Video,
    color: "#A855F7",
    bg: "rgba(168,85,247,0.12)",
  },
  {
    kind: MEDIA_KIND.AUDIO,
    label: "Audio",
    sub: `MP3, M4A, OGG · up to ${MEDIA_LIMITS[MEDIA_KIND.AUDIO].maxLabel}`,
    icon: Music,
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.12)",
  },
  {
    kind: MEDIA_KIND.DOCUMENT,
    label: "Document",
    sub: `PDF, DOC, XLS, PPT · up to ${MEDIA_LIMITS[MEDIA_KIND.DOCUMENT].maxLabel}`,
    icon: FileText,
    color: "#10B981",
    bg: "rgba(16,185,129,0.12)",
  },
];

/**
 * MessageInput — squared WhatsApp-style with attach menu + emoji picker.
 *
 * `onFileSelect(event, expectedKind)` allows the parent to reject files that
 * don't match the slot the user explicitly chose (e.g. "Photos" but a PDF).
 */
const MessageInput = ({
  messageText,
  setMessageText,
  selectedFile,
  filePreview,
  isWindowOpen,
  sending,
  uploadingMedia,
  onFileSelect,
  onRemoveFile,
  onSendMessage,
  replyingTo,
  onCancelReply,
  onOpenTemplate,
  onOpenProducts,
}) => {
  const textareaRef = useRef(null);
  const attachMenuRef = useRef(null);
  const emojiPanelRef = useRef(null);
  const fileInputsRef = useRef({});
  const replyText = replyingTo ? clamp(getReplyPreviewText(replyingTo)) : "";

  const [attachOpen, setAttachOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);

  // Close any open popover when the conversation window closes.
  useEffect(() => {
    if (!isWindowOpen) {
      setAttachOpen(false);
      setEmojiOpen(false);
    }
  }, [isWindowOpen]);

  // Click-outside handlers — registered only while open to avoid wasted listeners.
  useEffect(() => {
    if (!attachOpen) return;
    const handler = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setAttachOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [attachOpen]);

  useEffect(() => {
    if (!emojiOpen) return;
    const handler = (e) => {
      if (emojiPanelRef.current && !emojiPanelRef.current.contains(e.target)) {
        setEmojiOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [emojiOpen]);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSendMessage(e);
    }
  };

  const handlePickKind = (kind) => {
    setAttachOpen(false);
    const input = fileInputsRef.current[kind];
    if (input) input.click();
  };

  const handleEmojiClick = (data) => {
    const emoji = data?.emoji;
    if (!emoji) return;
    const ta = textareaRef.current;
    if (ta && document.activeElement === ta) {
      const start = ta.selectionStart ?? messageText.length;
      const end = ta.selectionEnd ?? messageText.length;
      const next = messageText.slice(0, start) + emoji + messageText.slice(end);
      setMessageText(next);
      // Restore caret right after the inserted emoji on next paint.
      requestAnimationFrame(() => {
        const pos = start + emoji.length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
        autoResize(ta);
      });
    } else {
      setMessageText((prev) => prev + emoji);
    }
  };

  const canSend = isWindowOpen && !sending && !uploadingMedia && (messageText.trim() || selectedFile);

  return (
    <div className="mi-root">
      {/* Window closed warning */}
      {!isWindowOpen && (
        <div className="mi-closed-warn">
          <AlertCircle size={13} className="mi-warn-icon" />
          <span className="mi-closed-warn-text">
            Window closed — use template messages to re-engage this customer.
          </span>
          {onOpenTemplate && (
            <button
              type="button"
              onClick={onOpenTemplate}
              className="mi-template-cta"
            >
              <LayoutTemplate size={13} />
              Send template
            </button>
          )}
        </div>
      )}

      {/* Reply preview strip */}
      {replyingTo && (
        <div className="mi-reply-strip">
          <div className="mi-reply-accent" />
          <CornerDownRight size={13} className="mi-reply-chevron" />
          <div className="mi-reply-body">
            <span className="mi-reply-sender">Replying to {getSenderLabel(replyingTo)}</span>
            <span className="mi-reply-text">{replyText}</span>
          </div>
          <button onClick={onCancelReply} className="mi-reply-close" aria-label="Cancel reply">
            <X size={14} />
          </button>
        </div>
      )}

      {/* File preview */}
      {filePreview && selectedFile && (
        <div className="mi-file-preview">
          <div className="mi-file-preview-inner">
            {filePreview.type === "image" && (
              <>
                <img src={filePreview.url} alt="Preview" className="mi-file-thumb" />
                <span className="mi-file-icon">{PREVIEW_ICONS.image}</span>
              </>
            )}
            {filePreview.type === "video" && (
              <>
                <video src={filePreview.url} className="mi-file-thumb" />
                <span className="mi-file-icon">{PREVIEW_ICONS.video}</span>
              </>
            )}
            {filePreview.type === "audio" && (
              <>
                <span className="mi-file-icon">{PREVIEW_ICONS.audio}</span>
                <audio src={filePreview.url} controls className="mi-audio" />
              </>
            )}
            {filePreview.type === "document" && (
              <span className="mi-file-icon">{PREVIEW_ICONS.document}</span>
            )}
            <div className="mi-file-info">
              <span className="mi-file-name">{selectedFile.name}</span>
              <span className="mi-file-size">{(selectedFile.size / 1024).toFixed(1)} KB</span>
            </div>
            <button onClick={onRemoveFile} className="mi-file-remove" aria-label="Remove file">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Hidden inputs — one per media kind so the OS picker shows the right filter. */}
      {ATTACH_MENU.map(({ kind }) => (
        <input
          key={kind}
          ref={(el) => { if (el) fileInputsRef.current[kind] = el; }}
          type="file"
          accept={MEDIA_LIMITS[kind].accept}
          onChange={(e) => onFileSelect(e, kind)}
          className="mi-file-input"
          aria-hidden="true"
          tabIndex={-1}
        />
      ))}

      {/* Input row */}
      <form onSubmit={onSendMessage} className="mi-form">
        {/* Template (always available — required when window is closed) */}
        {onOpenTemplate && (
          <button
            type="button"
            className={`mi-side-btn${!isWindowOpen ? " mi-side-btn--template" : ""}`}
            title="Send template"
            onClick={onOpenTemplate}
          >
            <LayoutTemplate size={20} />
          </button>
        )}

        {/* Emoji */}
        <div className="mi-popover-wrap" ref={emojiPanelRef}>
          <button
            type="button"
            className={`mi-side-btn${emojiOpen ? " mi-side-btn--active" : ""}`}
            title="Emoji"
            disabled={!isWindowOpen}
            onClick={() => { setEmojiOpen((v) => !v); setAttachOpen(false); }}
          >
            <Smile size={20} />
          </button>
          {emojiOpen && (
            <div className="mi-emoji-panel">
              <Suspense
                fallback={
                  <div className="mi-emoji-loading">
                    <Loader2 size={20} className="mi-spin" />
                  </div>
                }
              >
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  width={320}
                  height={380}
                  searchPlaceHolder="Search emoji"
                  previewConfig={{ showPreview: false }}
                  skinTonesDisabled
                  lazyLoadEmojis
                />
              </Suspense>
            </div>
          )}
        </div>

        {/* Composer box */}
        <div className="mi-composer">
          {/* Attach */}
          <div className="mi-popover-wrap" ref={attachMenuRef}>
            <button
              type="button"
              className={`mi-attach-btn${
                !isWindowOpen || sending || uploadingMedia ? " mi-attach-btn--disabled" : ""
              }${attachOpen ? " mi-attach-btn--active" : ""}`}
              title="Attach file"
              disabled={!isWindowOpen || sending || uploadingMedia}
              onClick={() => { setAttachOpen((v) => !v); setEmojiOpen(false); }}
              aria-haspopup="menu"
              aria-expanded={attachOpen}
            >
              <Paperclip size={17} />
            </button>
            {attachOpen && (
              <div className="mi-attach-menu" role="menu">
                {ATTACH_MENU.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.kind}
                      type="button"
                      role="menuitem"
                      className="mi-attach-item"
                      onClick={() => handlePickKind(item.kind)}
                    >
                      <span
                        className="mi-attach-item-icon"
                        style={{ background: item.bg, color: item.color }}
                      >
                        <Icon size={16} />
                      </span>
                      <span className="mi-attach-item-text">
                        <span className="mi-attach-item-label">{item.label}</span>
                        <span className="mi-attach-item-sub">{item.sub}</span>
                      </span>
                    </button>
                  );
                })}
                {onOpenProducts && (
                  <button
                    type="button"
                    role="menuitem"
                    className="mi-attach-item"
                    onClick={() => {
                      setAttachOpen(false);
                      onOpenProducts();
                    }}
                  >
                    <span
                      className="mi-attach-item-icon"
                      style={{ background: "rgba(16,185,129,0.12)", color: "#059669" }}
                    >
                      <ShoppingBag size={16} />
                    </span>
                    <span className="mi-attach-item-text">
                      <span className="mi-attach-item-label">Product</span>
                      <span className="mi-attach-item-sub">Send from connected catalog</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>

          <textarea
            ref={textareaRef}
            rows={1}
            value={messageText}
            onChange={(e) => { setMessageText(e.target.value); autoResize(e.target); }}
            onKeyDown={handleKeyDown}
            disabled={!isWindowOpen || sending || uploadingMedia}
            placeholder={
              !isWindowOpen
                ? "Window closed — use templates"
                : selectedFile
                ? "Add a caption (optional)…"
                : "Type a message"
            }
            className="mi-textarea"
            aria-label="Message input"
          />
        </div>

        {/* Send / mic */}
        {canSend ? (
          <button type="submit" className="mi-send-btn" aria-label="Send">
            {sending || uploadingMedia
              ? <Loader2 size={18} className="mi-spin" />
              : <Send size={18} />}
          </button>
        ) : (
          <button type="button" className="mi-side-btn" disabled={!isWindowOpen} title="Voice message">
            <Mic size={20} />
          </button>
        )}
      </form>

      <style>{`
        .mi-root {
          --mi-green: #00A884;
          --mi-green-dk: #008069;
          --mi-border: rgba(0,0,0,0.09);
          --mi-text: #111b21;
          --mi-text-2: #54656f;
          --mi-text-3: #8696a0;
          --mi-surface: #f0f2f5;
          --mi-radius: 4px;
          background: var(--mi-surface);
          border-top: 0.5px solid var(--mi-border);
          flex-shrink: 0;
          font-family: 'Segoe UI', system-ui, sans-serif;
          position: relative;
          padding-bottom: max(0px, env(safe-area-inset-bottom, 0px));
        }

        /* Closed warning */
        .mi-closed-warn {
          display: flex; align-items: center; gap: 7px;
          padding: 7px 14px; background: #fffbeb; border-bottom: 0.5px solid #fde68a;
          font-size: 11.5px; color: #92400e;
        }
        .mi-closed-warn-text { flex: 1; min-width: 0; }
        .mi-template-cta {
          flex-shrink: 0; display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 10px; border-radius: 4px; border: 1px solid #fcd34d;
          background: #fef3c7; color: #92400e; font-size: 11px; font-weight: 700;
          cursor: pointer; transition: background 0.15s;
        }
        .mi-template-cta:hover { background: #fde68a; }
        .mi-warn-icon { color: #d97706; flex-shrink: 0; }

        /* Reply strip */
        .mi-reply-strip {
          display: flex; align-items: stretch; gap: 8px;
          margin: 8px 14px 0; border-radius: var(--mi-radius);
          background: rgba(255,255,255,0.85); border: 0.5px solid var(--mi-border); overflow: hidden;
        }
        .mi-reply-accent { width: 3px; background: var(--mi-green); flex-shrink: 0; }
        .mi-reply-chevron { color: var(--mi-green); flex-shrink: 0; align-self: center; margin-left: 2px; }
        .mi-reply-body { flex: 1; min-width: 0; padding: 6px 0; display: flex; flex-direction: column; gap: 1px; }
        .mi-reply-sender { font-size: 11px; font-weight: 700; color: var(--mi-green-dk); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mi-reply-text { font-size: 11.5px; color: var(--mi-text-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mi-reply-close { background: none; border: none; cursor: pointer; color: var(--mi-text-3); padding: 6px 10px; display: flex; align-items: center; transition: color 0.15s; }
        .mi-reply-close:hover { color: var(--mi-text); }

        /* File preview */
        .mi-file-preview { margin: 8px 14px 0; }
        .mi-file-preview-inner {
          display: flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.9); border: 0.5px solid var(--mi-border);
          border-radius: var(--mi-radius); padding: 7px 10px;
        }
        .mi-file-thumb { width: 44px; height: 44px; object-fit: cover; border-radius: var(--mi-radius); flex-shrink: 0; }
        .mi-file-icon { color: var(--mi-text-3); flex-shrink: 0; display: flex; }
        .mi-audio { flex: 1; max-width: 200px; }
        .mi-file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .mi-file-name { font-size: 12px; font-weight: 600; color: var(--mi-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mi-file-size { font-size: 10.5px; color: var(--mi-text-3); }
        .mi-file-remove { background: none; border: none; cursor: pointer; color: var(--mi-text-3); padding: 4px; border-radius: 3px; display: flex; }
        .mi-file-remove:hover { background: rgba(0,0,0,0.06); color: var(--mi-text); }

        /* Form row */
        .mi-form { display: flex; align-items: flex-end; gap: 6px; padding: 8px 10px; }

        .mi-popover-wrap { position: relative; display: flex; align-items: flex-end; }

        .mi-side-btn {
          width: 40px; height: 40px; flex-shrink: 0; border-radius: 50%; border: none;
          background: none; color: var(--mi-text-3); cursor: pointer; display: flex;
          align-items: center; justify-content: center; transition: all 0.15s;
        }
        .mi-side-btn:hover:not(:disabled) { background: rgba(0,0,0,0.06); color: var(--mi-text-2); }
        .mi-side-btn--active { background: rgba(0,0,0,0.08); color: var(--mi-text); }
        .mi-side-btn--template { color: #b45309; background: rgba(251, 191, 36, 0.15); }
        .mi-side-btn--template:hover:not(:disabled) { background: rgba(251, 191, 36, 0.28); color: #92400e; }
        .mi-side-btn:disabled { opacity: 0.4; cursor: not-allowed; }

        /* Composer */
        .mi-composer {
          flex: 1; display: flex; align-items: flex-end; gap: 4px;
          background: #fff; border-radius: var(--mi-radius); border: 0.5px solid var(--mi-border);
          padding: 6px 10px; min-width: 0;
        }
        .mi-attach-btn {
          flex-shrink: 0; display: flex; align-items: center; justify-content: center;
          color: var(--mi-text-3); cursor: pointer; padding: 4px;
          border: none; background: transparent; border-radius: 4px; transition: all 0.15s;
        }
        .mi-attach-btn:hover:not(:disabled) { color: var(--mi-text-2); background: rgba(0,0,0,0.05); }
        .mi-attach-btn--active { color: var(--mi-text); background: rgba(0,0,0,0.06); }
        .mi-attach-btn--disabled,
        .mi-attach-btn:disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
        .mi-file-input { display: none; }

        .mi-textarea {
          flex: 1; border: none; outline: none; background: transparent;
          font-size: 13.5px; color: var(--mi-text); line-height: 1.5;
          resize: none; max-height: 120px; font-family: inherit; min-width: 0;
          padding: 2px 0;
          scrollbar-width: thin;
        }
        .mi-textarea::placeholder { color: var(--mi-text-3); }
        .mi-textarea:disabled { cursor: not-allowed; }

        /* Attach menu (popover above the paperclip) */
        .mi-attach-menu {
          position: absolute; bottom: calc(100% + 8px); left: -4px;
          width: 270px; padding: 6px;
          background: #fff; border: 0.5px solid var(--mi-border);
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06);
          display: flex; flex-direction: column; gap: 2px;
          z-index: 40;
          animation: mi-pop 0.12s ease-out;
        }
        .mi-attach-item {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 10px; border: none; background: transparent;
          border-radius: 8px; cursor: pointer; text-align: left;
          font-family: inherit; transition: background 0.12s;
        }
        .mi-attach-item:hover { background: rgba(0,0,0,0.04); }
        .mi-attach-item-icon {
          width: 34px; height: 34px; border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .mi-attach-item-text { display: flex; flex-direction: column; min-width: 0; }
        .mi-attach-item-label { font-size: 13px; font-weight: 600; color: var(--mi-text); }
        .mi-attach-item-sub   { font-size: 11px; color: var(--mi-text-3); margin-top: 1px; }

        /* Emoji panel — positioned above the smile button */
        .mi-emoji-panel {
          position: absolute; bottom: calc(100% + 8px); left: 0;
          background: #fff; border: 0.5px solid var(--mi-border);
          border-radius: 10px; overflow: hidden;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06);
          z-index: 40;
          animation: mi-pop 0.12s ease-out;
        }
        .mi-emoji-loading {
          width: 320px; height: 380px;
          display: flex; align-items: center; justify-content: center;
          color: var(--mi-text-3);
        }

        @keyframes mi-pop {
          from { opacity: 0; transform: translateY(4px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }

        /* Send button */
        .mi-send-btn {
          width: 42px; height: 42px; flex-shrink: 0; border-radius: 50%;
          background: var(--mi-green); color: #fff; border: none;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: background 0.15s;
        }
        .mi-send-btn:hover { background: var(--mi-green-dk); }

        .mi-spin { animation: mi-rotate 0.7s linear infinite; }
        @keyframes mi-rotate { to { transform: rotate(360deg); } }

        /* Mobile — keep the popovers on-screen */
        @media (max-width: 480px) {
          .mi-attach-menu { width: 240px; }
          .mi-emoji-panel :global(.EmojiPickerReact) { width: 280px !important; }
        }
      `}</style>
    </div>
  );
};

export default MessageInput;
